import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { pollPaymentStatus, refreshProStatus, getEmail, setIsPro } from "@/lib/pro";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useT } from "@/i18n";

export default function PaymentSuccess() {
  const { t } = useT();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const sessionId = params.get("session_id");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!sessionId) {
        setStatus("failed");
        return;
      }
      const r = await pollPaymentStatus(sessionId);
      if (cancelled) return;
      if (r.payment_status === "paid") {
        setStatus("paid");
        // Refresh pro status locally
        await refreshProStatus(r.email || getEmail());
        setIsPro(true);
      } else {
        setStatus(r.payment_status || "failed");
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen w-full grid-bg flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-black/60 backdrop-blur-2xl border border-white/10 p-10">
        {status === "polling" && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="animate-spin text-cyan-300" size={40} />
            <div className="font-display text-3xl tracking-tighter text-white">{t("payment.finalizing")}</div>
            <div className="text-xs tracking-[0.3em] text-white/60">{t("payment.confirming")}</div>
          </div>
        )}
        {status === "paid" && (
          <div className="flex flex-col items-center gap-4 text-center">
            <CheckCircle2 className="text-[#39FF14]" size={48} />
            <div className="font-display text-4xl tracking-tighter text-[#FFD400]" style={{ textShadow: "0 0 24px rgba(255,212,0,0.6)" }}>
              {t("payment.welcome_pro")}
            </div>
            <div className="text-sm text-white/80 tracking-wide">
              {t("payment.welcome_desc")}
            </div>
            <button
              data-testid="success-play-button"
              onClick={() => nav("/")}
              className="mt-4 px-6 py-3 border border-[#FFD400] text-[#FFD400] hover:bg-[#FFD400] hover:text-black transition-colors text-xs tracking-[0.3em]"
            >
              {t("payment.start_escaping")}
            </button>
          </div>
        )}
        {(status === "failed" || status === "expired" || status === "timeout") && (
          <div className="flex flex-col items-center gap-4 text-center">
            <XCircle className="text-[#FF0055]" size={48} />
            <div className="font-display text-3xl tracking-tighter text-white">{t("payment.not_confirmed")}</div>
            <div className="text-sm text-white/60 tracking-wide">
              {t("payment.not_confirmed_desc")}
            </div>
            <button
              onClick={() => nav("/")}
              className="mt-4 px-6 py-3 border border-white/30 text-xs tracking-[0.3em] hover:border-white hover:text-white transition-colors"
            >
              {t("payment.back_menu")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
